import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import EmptyState from "../components/EmptyState";
import { Bell, CheckCheck } from "lucide-react";
import { formatDateTime } from "../lib/format";

const SEVERITY_STYLE = {
    info: "border-l-muted-foreground",
    important: "border-l-accent",
    critical: "border-l-destructive",
};

const Alerts = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/alerts");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const markAll = async () => {
        await api.post("/alerts/mark_all_read");
        load();
    };

    const markRead = async (id) => {
        await api.post(`/alerts/${id}/read`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="alerts-page">
            <header className="flex items-end justify-between gap-4">
                <div>
                    <h1 className="font-grotesk text-2xl md:text-3xl font-semibold">Alerts</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Dashboardnotificaties voor nieuwe deals, prijsverlagingen en hoge Flip Scores.
                    </p>
                </div>
                {items.some((a) => !a.read) && (
                    <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read">
                        <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
                    </Button>
                )}
            </header>
            {loading ? (
                <Skeleton className="h-40 w-full" />
            ) : items.length === 0 ? (
                <EmptyState
                    icon={Bell}
                    title="Geen alerts"
                    description="Alerts worden gegenereerd zodra scrapers nieuwe data leveren en thresholds (zoals Flip Score ≥ 75 of ROI ≥ 20%) overschrijden."
                    testid="alerts-empty"
                />
            ) : (
                <div className="space-y-2">
                    {items.map((a) => (
                        <Card
                            key={a.id}
                            data-testid={`alert-${a.id}`}
                            className={`p-4 border-l-4 ${SEVERITY_STYLE[a.severity] || ""} ${a.read ? "opacity-60" : ""}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="flex-1">
                                    <div className="font-grotesk font-medium text-sm">{a.title}</div>
                                    <p className="text-sm text-muted-foreground mt-1">{a.body}</p>
                                    <div className="text-[11px] text-muted-foreground mt-1.5 uppercase tracking-wider">
                                        {a.type.replace(/_/g, " ")} · {formatDateTime(a.created_at)}
                                    </div>
                                </div>
                                {!a.read && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => markRead(a.id)}
                                        data-testid={`mark-read-${a.id}`}
                                    >
                                        Mark read
                                    </Button>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Alerts;
